import {
  createDepartment,
  createDepartmentLevel,
  createPointRewardConfiguration,
  createSkill,
  createSkillTrack,
  deleteSkillTrack,
  updateDepartment,
  updateDepartmentLevel,
  updateOrganizationSetting,
  updatePointRewardConfiguration,
  updateSkill,
  updateSkillTrack,
  createTag,
  updateTag,
  removeSkill,
  deleteDepartment,
} from "@/graphql/mutations";
import {
  getOrganization,
  getOrganizationSetting,
  getSlackOAuthUrl,
  listDepartmentLevels,
  listOrganizationSettings,
  listPointRewardConfigurations,
} from "@/graphql/queries";
import { GraphQLQuery } from "@aws-amplify/api";
import { useQuery } from "@tanstack/react-query";
import { notification } from "antd";
import { API, graphqlOperation } from "aws-amplify";

import { sortRecordsByKey } from "@/utils/sortGraphqlRecords";
import { customListUserExternalNotificationSettings } from "@/graphql/customQueries/listUserExternalNotificationSettings";
import {
  EnumSkillContext,
} from "@/graphql/graphql";
import useAuthStore from "@/store/useAuthStore";

export interface IsetPointRewardConfig {
  method: "create" | "update";
  amount: number;
  eventType:
  | "JOIN_EVENT"
  | "JOIN_CIRCLE"
  | "BECOME_A_FAN"
  | "UPGRADE_TO_ALLIES_CONNECTION"
  | "ACCEPT_INTRODUCTION_REQUEST"
  | "ACCEPT_SKILL_VERIFICATION_REQUEST"
  | "VERIFY_SKILL"
  | "ANSWER_QUICK_QUESTION"
  | "ACCEPTED_COC_INVITATION"
  | "ACCEPTED_SKILLTRACK_RECOMMENDATION"
  | "ACCEPTED_SKILL_RECOMMENDATION"
  | "ACCEPTED_PENDING_ENDORSEMENT"
  | "ACCEPTED_EVENT_INVITATION"
  | "ACCEPTED_CIRCLE_INVITATION"
  | "ACCEPTED_CONNECTION_RECOMMENDATION_REQUEST"
  | "WRITE_ENDORSEMENT"
  | "RATE_RELATIONSHIP"
  | "RECOMMEND_CONNECTION"
  | "RECOMMEND_SKILL_GOALS"
  | "RECOMMEND_EVENT"
  | "RECOMMEND_CIRCLE"
  | "INVITE_TO_COC"
  | "INVITE_TO_CIRCLE"
  | "INVITE_TO_EVENT"
  | "CREATE_EVENT"
  | "CREATE_CIRCLE"
  | "ADD_SKILL_GOALS"
  | "SELECTED_FOCUSED_MILESTONE";
  _version?: number;
  organizationGroup?: string;
  id?: string;
  category?: string | string[];
}

export const setPointRewardConfig = async (payload: IsetPointRewardConfig) => {
  const methodPost = {
    create: createPointRewardConfiguration,
    update: updatePointRewardConfiguration,
  };
  const payloadFiltered = {
    amount: payload.amount || 0,
    category: payload.category,
    EventType: payload.eventType,
    id: payload.id,
    organizationGroup: payload.organizationGroup,
    _version: payload._version,
  };
  const records = await API.graphql<GraphQLQuery<any>>({
    query: methodPost[payload?.method],
    variables: { input: payloadFiltered },
  });
  return records;
};

export const useGetListPointRewardConf = () => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_LIST_POINT_REWARD_CONFIGURATIONS"], // Corrected queryKey
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(listPointRewardConfigurations, {
          limit: 1000,
        }),
      );
      const records: any[] =
        response?.data?.listPointRewardConfigurations?.items ?? [];
      return records;
    },
  });
};

interface KeyValueObject {
  key: string;
  value: string;
}

export interface IOrgSettings {
  id: string;
  organizationGroup: string;
  updatedBy?: string; // Optional field
  disclaimer?: string; // Optional field
  waivers?: string; // Optional field
  privacyPolicy?: string; // Optional field
  termsConditions?: string; // Optional field
  allowAllCreateCircle: boolean;
  allowAllCreateEvent: boolean;
  maxNoOfMinIntroducers: number;
  onboarding?: KeyValueObject[]; // Optional field, an array of KeyValueObject
  slackBotToken: string | null;
  _version: number;
  pointTiers: {
    bronze: number;
    gold: number;
    platinum: number;
    silver: number;
  };
}

export const useGetOrgSettings = (
  organizationGroup: string,
  options: { enabled?: boolean; refetchOnWindowFocus?: boolean } = {}
) => {
  return useQuery({
    queryKey: ["KEY_GET_ORG_SETTINGS", organizationGroup],
    queryFn: async () => {
      if (!organizationGroup) {
        throw new Error("Organization group is not available for the current user.");
      }
      const response: any = await API.graphql(
        graphqlOperation(getOrganizationSetting, {
          id: organizationGroup,
        }),
      );
      return [response?.data?.getOrganizationSetting as IOrgSettings];
    },
    enabled: options.enabled ?? !!organizationGroup,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  });
};

export const setOrgSettings = async (payload: any) => {
  try {
    const { data }: any = await API.graphql({
      query: updateOrganizationSetting,
      variables: {
        input: {
          ...payload,
        },
      },
    });
    return data?.updateOrganizationSetting as IOrgSettings;
  } catch (error: any) {
    console.log(error);
    notification.error({ message: error?.errors[0]?.message });
    // return error.message || error?.errors[0]?.message;
    throw error;
  }
};

export const createNewDepartment = async (
  name: string,
  id?: string,
  _version?: number,
  isEdit?: boolean,
) => {
  try {
    const res: any = await API.graphql({
      query: isEdit ? updateDepartment : createDepartment,
      variables: {
        input: {
          name,
          id,
          _version,
        },
      },
    });
    return isEdit ? res?.data?.updateDepartment : res?.data?.createDepartment;
  } catch (error) {
    return error;
  }
};

export const createNewExpertise = async (
  name: string,
  organizationGroup: string,
  id?: string,
  _version?: number,
  isEdit?: boolean,
) => {
  try {
    const res: any = await API.graphql({
      query: isEdit ? updateTag : createTag,
      variables: {
        input: {
          organizationGroup,
          name,
          id,
          type: "EXPERTISE",
          _version,
        },
      },
    });
    return isEdit ? res?.data?.updateTag : res?.data?.createTag;
  } catch (error) {
    return error;
  }
};

export const createNewSkillTrack = async (
  name: string,
  color: string,
  departmentId: string,
  id?: string,
  _version?: number,
) => {
  try {
    const res: any = await API.graphql({
      query: id ? updateSkillTrack : createSkillTrack,
      variables: {
        input: {
          name,
          _version,
          id,
          color,
          departmentId,
        },
      },
    });
    return res;
  } catch (error) {
    console.log(error);
    return error;
  }
};

export const deleteSkilltrack = async (id: string) => {
  const res: any = await API.graphql({
    query: deleteSkillTrack,
    variables: {
      input: {
        id,
      },
    },
  });
};

export const createSkillLevel = async (
  departmentId: string,
  name: string,
  rank?: number,
  id?: string,
  _version?: number,
) => {
  try {
    const res: any = await API.graphql({
      query: id ? updateDepartmentLevel : createDepartmentLevel,
      variables: {
        input: {
          departmentId,
          id,
          rank,
          name,
          _version,
        },
      },
    });
    return res;
  } catch (error) {
    console.log(error);
  }
};

export const useListLevel = () => {
  return useQuery({
    queryKey: ["KEY_GET_LEVELS"],
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(listDepartmentLevels),
      );
      const records = response?.data?.listDepartmentLevels?.items;
      return sortRecordsByKey(records, "name");
    },
  });
};

export const createNewSkill = async (
  skillDepartmentId: string,
  departmentlevelId: string,
  skillTrackId: string,
  name: string,
  description: string,
  trackOrder: number,
  id?: string,
  _version?: number,
) => {
  try {
    const res: any = await API.graphql({
      query: id ? updateSkill : createSkill,
      variables: {
        input: {
          _version,
          id,
          departmentlevelId,
          name,
          description,
          trackOrder,
          skillTrackId,
          skillDepartmentId,
        },
      },
    });
    return res;
  } catch (error) {
    console.log(error);
  }
};

const fetchSlackOAuthUrl = async (isInit: boolean) => {
  const response: any = await API.graphql(
    graphqlOperation(getSlackOAuthUrl, { isInit: isInit }),
  );
  const records: any = response?.data?.getSlackOAuthUrl ?? {};
  return records;
};

export const useGetSlackOAuthUrl = (isInit: boolean) => {
  return useQuery({
    queryKey: ["KEY_GET_SLACK_OAUTH_URL"],
    queryFn: () => fetchSlackOAuthUrl(isInit),
    enabled: false,
  });
};

export interface IListUserExternalNotificationSettings {
  filter: {
    userID: {
      eq?: string;
    };
  };
}

const fetchListUserExternalNotifSettings = async (userID?: string) => {
  const variables: IListUserExternalNotificationSettings = {
    filter: {
      userID: { eq: userID },
    },
  };
  const response: any = await API.graphql(
    graphqlOperation(customListUserExternalNotificationSettings, variables),
  );
  const records: any =
    response?.data?.listUserExternalNotificationSettings ?? {};
  return records;
};

export const useGetListUserExternalNotifSettings = (userId?: string) => {
  return useQuery({
    queryKey: ["KEY_GET_LIST_USER_EXTERNAL_NOTIF_SETTINGS"],
    queryFn: () => fetchListUserExternalNotifSettings(userId),
    refetchOnWindowFocus: true,
  });
};

export const deleteSkillByContext = async (
  id: string,
  context: EnumSkillContext,
  isHR: boolean,
) => {
  const res: any = await API.graphql({
    query: removeSkill,
    variables: {
      id,
      context,
      isHR,
    },
  });
  return res;
};

export const deleteDepartmentById = async (id: string, _version: number) => {
  const res: any = await API.graphql({
    query: deleteDepartment,
    variables: {
      input: {
        id,
        _version,
      },
    },
  });
  return res;
};